/** Project rules shared by the Dashboard, Projects page and Management View (the original app's rules). */
import type { Project, ProjectStatus, ProjectUpdate } from '@/types/models';
import { daysLeft } from './format';

/** "Active" = Live, In Progress or Planning. */
export const ACTIVE_STATUSES: ProjectStatus[] = ['Live', 'In Progress', 'Planning'];
export const isActive = (p: Pick<Project, 'status'>): boolean => ACTIVE_STATUSES.includes(p.status);

/** Days without a Project Log before an active project counts as stale. */
export const STALE_DAYS = 2;

/** Current deadline: revised, else original, else ''. */
export const deadline = (p: Pick<Project, 'revised' | 'planned'>): string => p.revised || p.planned || '';

/** CSS class of a status badge (same mapping as the original). */
export const statusClass = (s: string): string =>
  (({ Live: 'live', 'In Progress': 'progress', Planning: 'planning', 'On Hold': 'hold', Delayed: 'delayed', Completed: 'completed' }) as Record<string, string>)[
    s
  ] || 'hold';

/** Not Completed and the current deadline is before today (the Dashboard's "Past deadline"). */
export const isOverdue = (p: Project): boolean => {
  const d = deadline(p);
  if (!d || p.status === 'Completed') return false;
  const n = daysLeft(d);
  return n !== null && n < 0;
};

/** Latest Project Log date of a project (all time), or ''. */
export const lastUpdateDate = (projectId: string, updates: ProjectUpdate[]): string =>
  updates.filter((u) => u.projectId === projectId).reduce((m, u) => (u.date && u.date > m ? u.date : m), '');

/** Active and no Project Log for 2+ days (or none). */
export const isStale = (p: Project, updates: ProjectUpdate[]): boolean => {
  if (!isActive(p)) return false;
  const last = lastUpdateDate(p.id, updates);
  if (!last) return true;
  return -(daysLeft(last) ?? 0) >= STALE_DAYS;
};
